import {http, HttpResponse} from 'msw';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import type {CheckoutProfilesMockState} from './createCheckoutProfilesMockState';
import {getCheckoutProfileRuleConflicts} from './previewCheckoutProfileRules';

const gridCodes = ['checkout-profile', 'checkout-rule', 'checkout-profile-rule'] as const;
type GridCode = (typeof gridCodes)[number];
const querySchema = z.object({
  smartSearch: z.string().nullable().optional(),
  filters: z
    .record(
      z.union([
        z.string(),
        z.boolean(),
        z.number(),
        z.array(z.string()),
        z.object({from: z.string().optional(), to: z.string().optional()}),
      ])
    )
    .default({}),
  order: z.array(z.object({columnKey: z.string(), order: z.enum(['ASC', 'DESC'])})).default([]),
});
type GridQuery = z.infer<typeof querySchema>;
type GridValues = Record<string, string | boolean | number | string[] | null>;
type GridRecord = {id: string; values: GridValues};

const virtualPresetSchema = z.object({
  gridSettings: z.record(z.unknown()),
  columnsSettings: z.array(
    z
      .object({
        columnKey: z.string(),
        visible: z.boolean(),
        width: z.number(),
        pinned: z.enum(['LEFT', 'RIGHT']).nullable(),
      })
      .passthrough()
  ),
  dataQueryId: z.string(),
});

const profileColumns = [
  ['name', 'Name', 'TEXT'],
  ['isActive', 'Active', 'BOOLEAN'],
  ['isComplete', 'Complete', 'BOOLEAN'],
  ['branch', 'Branch', 'TEXT'],
  ['typeOfSale', 'Type of sale', 'TEXT'],
  ['saleRegimes', 'Sale regimes', 'TEXT'],
  ['depositsAllowed', 'Deposits', 'BOOLEAN'],
  ['allowedPaymentMethods', 'Allowed payment methods', 'TEXT'],
  ['defaultMethod', 'Default method', 'TEXT'],
] as const;
const ruleColumns = [
  ['name', 'Name', 'TEXT'],
  ['isActive', 'Active', 'BOOLEAN'],
  ['branch', 'Branch', 'TEXT'],
  ['checkoutProfile', 'Check-out profile', 'TEXT'],
  ['typeOfSale', 'Type of sale', 'TEXT'],
  ['sourceType', 'Source type', 'TEXT'],
  ['parentTypes', 'Service case type / Authorization profile', 'TEXT'],
  ['types', 'Type', 'TEXT'],
  ['variants', 'Variants', 'TEXT'],
  ['priority', 'Priority', 'INTEGER'],
  ['hasConflict', 'Conflict', 'BOOLEAN'],
  ['ruleAboveId', 'Rule above', 'TEXT'],
  ['ruleBelowId', 'Rule below', 'TEXT'],
] as const;
const hiddenColumns: string[] = ['ruleAboveId', 'ruleBelowId'];
const getColumns = (grid: GridCode) => (grid === 'checkout-profile' ? profileColumns : ruleColumns);
const getOrder = (grid: GridCode, query: GridQuery) =>
  query.order.length
    ? query.order
    : [
        {
          columnKey: grid === 'checkout-profile' ? 'name' : 'priority',
          order: grid === 'checkout-profile' ? ('ASC' as const) : ('DESC' as const),
        },
      ];

function getGridRecords(grid: GridCode, state: CheckoutProfilesMockState): GridRecord[] {
  if (grid === 'checkout-profile') {
    return [...state.profiles.values()].map((profile) => ({
      id: profile.id,
      values: {
        name: profile.name,
        isActive: profile.isActive,
        isComplete: profile.isComplete,
        branch: profile.branchId,
        branchId: profile.branchId,
        typeOfSale: profile.typeOfSale,
        saleRegimes:
          profile.typeOfSale === 'INTERNAL'
            ? []
            : [
                'DOMESTIC',
                ...(profile.saleRegimes?.inEurope ? ['IN_EUROPE'] : []),
                ...(profile.saleRegimes?.outOfEurope ? ['OUT_OF_EUROPE'] : []),
              ],
        depositsAllowed: profile.deposit?.isAllowed ?? false,
        allowedPaymentMethods: profile.payments?.methods.map((method) => method.method) ?? [],
        defaultMethod: profile.payments?.defaultMethod ?? '',
      },
    }));
  }
  // The backend owns the real neighbour scope; the mock orders every rule by priority.
  const orderedRuleIds = [...state.rules.values()]
    .sort((first, second) => second.priority - first.priority || first.id.localeCompare(second.id))
    .map((rule) => rule.id);
  return [...state.rules.values()].map((rule) => {
    const position = orderedRuleIds.indexOf(rule.id);
    const profile = state.profiles.get(rule.checkoutProfileId);
    const parentIds = rule.conditions.serviceCaseTypeIds ?? rule.conditions.authorizationProfileIds;
    const typeIds = rule.conditions.serviceOrderTypeIds ?? rule.conditions.directSaleTypeIds;
    const variantIds =
      rule.conditions.serviceOrderVariantIds ?? rule.conditions.directSaleVariantIds;
    return {
      id: rule.id,
      values: {
        name: rule.name,
        isActive: rule.isActive,
        branch: rule.branchId,
        branchId: rule.branchId,
        checkoutProfile: profile?.name ?? '',
        checkoutProfileId: rule.checkoutProfileId,
        typeOfSale: profile?.typeOfSale ?? '',
        sourceType: rule.sourceType,
        priority: rule.priority,
        parentTypes: parentIds?.map(
          (id) =>
            state.variants.find((variant) => variant.parentTypeId === id)?.parentTypeName ?? id
        ) ?? ['Any'],
        types: typeIds?.map(
          (id) => state.variants.find((variant) => variant.typeId === id)?.typeName ?? id
        ) ?? ['Any'],
        variants: variantIds?.map(
          (id) => state.variants.find((variant) => variant.id === id)?.name ?? id
        ) ?? ['Any'],
        hasConflict:
          getCheckoutProfileRuleConflicts(
            rule,
            [...state.rules.values()],
            state.profiles,
            state.variants
          ).length > 0,
        ruleAboveId: orderedRuleIds[position - 1] ?? null,
        ruleBelowId: orderedRuleIds[position + 1] ?? null,
      },
    };
  });
}

function matchesFilter(
  value: GridValues[string] | undefined,
  filter: GridQuery['filters'][string]
) {
  return match(filter)
    .with(
      Pattern.array(Pattern.string),
      (items) =>
        !items.length ||
        items.some((item) => (Array.isArray(value) ? value.includes(item) : String(value) === item))
    )
    .with(
      {from: Pattern.optional(Pattern.string), to: Pattern.optional(Pattern.string)},
      (range) => {
        const number = Number(value);
        return (
          (isNil(range.from) || number >= Number(range.from)) &&
          (isNil(range.to) || number <= Number(range.to))
        );
      }
    )
    .with(Pattern.string, (text) =>
      (Array.isArray(value) ? value : [String(value)]).some((item) =>
        item.toLowerCase().includes(text.toLowerCase())
      )
    )
    .otherwise((item) => String(value) === String(item));
}

function getFilteredRecords(grid: GridCode, state: CheckoutProfilesMockState, query: GridQuery) {
  const search = query.smartSearch?.toLowerCase();
  return getGridRecords(grid, state)
    .filter(
      (row) =>
        (!search || JSON.stringify(row.values).toLowerCase().includes(search)) &&
        Object.entries(query.filters).every(([key, filter]) =>
          matchesFilter(row.values[key], filter)
        )
    )
    .sort((first, second) => {
      for (const order of getOrder(grid, query)) {
        const left = first.values[order.columnKey];
        const right = second.values[order.columnKey];
        const comparison =
          typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right));
        if (comparison) {
          return order.order === 'DESC' ? -comparison : comparison;
        }
      }
      return first.id.localeCompare(second.id);
    });
}

const gridSettings = {
  headerHeight: 'DEFAULT',
  rowHeight: 'DEFAULT',
  valueWrapMode: 'TRUNCATE',
  horizontalSeparatorsVisible: true,
  verticalSeparatorsVisible: false,
  itemsPerPage: 20,
  keyboardNavigationEnabled: true,
  columnGroupsVisible: false,
  stripedRows: false,
  summaryRowVisible: false,
};

export function createCheckoutProfilesGridHandlers(state: CheckoutProfilesMockState) {
  return gridCodes.flatMap((grid) => {
    const base = `*/v6/data-grid/${grid}`;
    const defaultId = `default-${grid}`;
    const queries = new Map<string, GridQuery>([
      [defaultId, {filters: {}, order: [], smartSearch: null}],
    ]);
    const actions =
      grid === 'checkout-profile'
        ? [
            ['redirectDetail', 'Open'],
            ['duplicate', 'Duplicate'],
            ['setAsActive', 'Activate'],
            ['setAsInactive', 'Deactivate'],
            ['delete', 'Delete'],
          ]
        : [
            ['redirectDetail', 'Open'],
            ['setAsActive', 'Activate'],
            ['setAsInactive', 'Deactivate'],
            ['moveUp', 'Move up'],
            ['moveDown', 'Move down'],
            ['delete', 'Delete'],
          ];
    const definition = {
      columns: getColumns(grid).map(([key, title, type]) => ({
        key,
        title,
        type,
        renderer: {
          type: match(type)
            .with('BOOLEAN', () => 'CHECKBOX')
            .with('INTEGER', () => 'INTEGER')
            .otherwise(() => 'PLAIN'),
        },
        group: null,
        filter: {type: type === 'BOOLEAN' ? 'SWITCH' : 'TEXT', isDisabled: false, quickFilters: []},
        sortable: true,
        smartSearch: true,
        isArray: [
          'saleRegimes',
          'allowedPaymentMethods',
          'parentTypes',
          'types',
          'variants',
        ].includes(key),
        hidden: hiddenColumns.includes(key),
      })),
      columnGroups: [],
      actions: actions.map(([key, title]) => ({
        key,
        title,
        description: '',
        type: key === 'redirectDetail' ? 'ROW_CLICK' : 'SECONDARY',
        variant: null,
        icon: null,
        single: true,
        bulk: ['setAsActive', 'setAsInactive'].includes(key),
        isLink: key === 'redirectDetail',
      })),
      behavior: {
        settingsToolPanelVisible: true,
        columnToolPanelVisible: true,
        filterToolPanelVisible: true,
        actionColumnEnabled: true,
        rangeSelectAllowed: false,
        rowSelectMode: 'MULTIPLE',
        wrapperStyle: 'CARD',
        paginationEnabled: true,
        clipboardExportAllowed: false,
        columnMenuEnabled: true,
        columnMovingAllowed: true,
        columnPinningAllowed: true,
        columnSortMode: 'MULTI_SORT',
        contextMenuEnabled: false,
        columnResizingAllowed: true,
        fitToScreenAllowed: true,
        presetsEnabled: false,
      },
    };
    const defaultPreset = virtualPresetSchema.parse({
      gridSettings,
      columnsSettings: getColumns(grid).map(([columnKey]) => ({
        columnKey,
        visible: !hiddenColumns.includes(columnKey),
        width: 160,
        pinned: null,
      })),
      dataQueryId: defaultId,
    });
    let virtualPreset = structuredClone(defaultPreset);
    const readQuery = (id: string) => queries.get(id);
    const notFound = () =>
      HttpResponse.json(
        {errors: [{code: 'NOT_FOUND', message: 'Mock grid query not found'}]},
        {status: 404}
      );
    return [
      http.get(`${base}/definition`, () => HttpResponse.json(definition)),
      http.get(`${base}/preset`, () => HttpResponse.json([])),
      http.get(`${base}/virtual-preset`, () => HttpResponse.json(virtualPreset)),
      http.put(`${base}/virtual-preset`, async ({request}) => {
        const parsed = virtualPresetSchema.safeParse(await request.json());
        if (!parsed.success) {
          return HttpResponse.json({errors: parsed.error.issues}, {status: 400});
        }
        virtualPreset = parsed.data;
        return HttpResponse.json(virtualPreset);
      }),
      http.delete(`${base}/virtual-preset`, () => {
        virtualPreset = structuredClone(defaultPreset);
        return new HttpResponse(null, {status: 204});
      }),
      http.put(`${base}/preset/active`, () => new HttpResponse(null, {status: 204})),
      http.post(`${base}/data-query`, async ({request}) => {
        const parsed = querySchema.safeParse(await request.json());
        if (!parsed.success) {
          return HttpResponse.json({errors: parsed.error.issues}, {status: 400});
        }
        const dataQueryId = crypto.randomUUID();
        queries.set(dataQueryId, parsed.data);
        return HttpResponse.json({dataQueryId});
      }),
      http.get<{dataQueryId: string}>(`${base}/data-query/:dataQueryId`, ({params}) => {
        const query = readQuery(params.dataQueryId);
        return query
          ? HttpResponse.json({id: params.dataQueryId, ...query, order: getOrder(grid, query)})
          : notFound();
      }),
      http.get<{dataQueryId: string}>(`${base}/data/:dataQueryId/count`, ({params}) => {
        const query = readQuery(params.dataQueryId);
        return query
          ? HttpResponse.json({totalRows: getFilteredRecords(grid, state, query).length})
          : notFound();
      }),
      http.post<{dataQueryId: string}>(`${base}/data/:dataQueryId`, ({params, request}) => {
        const query = readQuery(params.dataQueryId);
        if (isNil(query)) {
          return notFound();
        }
        const search = new URL(request.url).searchParams;
        const offset = Math.max(0, Number(search.get('offset') ?? 0));
        const limit = Math.max(0, Number(search.get('limit') ?? gridSettings.itemsPerPage));
        const rows = getFilteredRecords(grid, state, query)
          .slice(offset, offset + limit)
          .map((row, index) => ({
            id: row.id,
            rowNumber: offset + index + 1,
            cells: Object.fromEntries(
              Object.entries(row.values).map(([key, value]) => [key, {value, style: {}}])
            ),
            actions: {
              primary: [],
              rowClick: 'redirectDetail',
              secondary: [
                ...(grid === 'checkout-profile' ? ['duplicate'] : []),
                row.values.isActive ? 'setAsInactive' : 'setAsActive',
                ...(isNil(row.values.ruleAboveId) ? [] : ['moveUp']),
                ...(isNil(row.values.ruleBelowId) ? [] : ['moveDown']),
                'delete',
              ],
            },
          }));
        return HttpResponse.json({rows});
      }),
    ];
  });
}
